import { useEffect, useState } from 'react'
import { createConsumer, type Consumer } from '@rails/actioncable'
import { peekPreloaded } from '@/lib/preloaded'

export type Discipline =
  | 'all'
  | 'cycling'
  | 'running'
  | 'walking'
  | 'pilates'
  | 'yoga'
  | 'stretching'
  | 'meditation'
  | 'cardio'
  | 'strength'

export interface RankedArtist {
  id: string
  name: string
  image_url: string | null
  play_count: number
}

export interface RankedInstructor {
  id: string
  name: string
  image_url: string | null
  ride_count: number
}

export interface NamedCount {
  name: string
  count: number
}

export interface OverplayedSong {
  id: string
  title: string
  artists: string[]
  ride_count: number
  album_image_url: string | null
}

export interface VersatileArtist {
  id: string
  name: string
  image_url: string | null
  genre_count: number
}

export interface ActivityEntry {
  id: string
  title: string
  instructor_name: string | null
  synced_at: string
  song_count: number
  fitness_discipline: Exclude<Discipline, 'all'>
}

export interface DisciplineStats {
  totals: {
    rides: number
    songs: number
    artists: number
    albums: number
    instructors: number
    one_hit_artists: number
    total_ride_hours: number
    total_song_plays: number
    avg_songs_per_ride: number
    explicit_percent: number
  }
  scrape_progress: {
    rides_synced: number
    rides_detailed: number
    percent: number
  }
  top_artists: RankedArtist[]
  top_instructors: RankedInstructor[]
  genres: NamedCount[]
  ride_types: NamedCount[]
  most_overplayed_song: OverplayedSong | null
  most_versatile_artist: VersatileArtist | null
  recent_activity: ActivityEntry[]
  last_synced_at: string | null
}

export type DashboardData = Record<Discipline, DisciplineStats>

interface DashboardMessage {
  // Omitted on most broadcasts -- the broadcaster throttles the expensive
  // full recompute to at most once every ~10s, independent of how often
  // individual rides finish syncing. Always present on the initial
  // subscribe transmit.
  stats?: DashboardData
  activity: ActivityEntry | null
}

const ACTIVITY_FEED_LIMIT = 15

// A single shared consumer for the module's lifetime -- React StrictMode's
// double-invoke of effects is handled by unsubscribing on cleanup, not by
// avoiding a second createConsumer call. Created on first use (inside an
// effect) so importing this module on the server never touches the browser.
let consumer: Consumer | null = null

// Last-received snapshot, so coming back to the dashboard paints instantly
// (the subscription then refreshes it) instead of starting from skeletons --
// which is also what lets a shared avatar morph back into its leaderboard row.
let lastData: DashboardData | null = null
let lastActivity: ActivityEntry[] = []

export function useDashboardChannel() {
  const [data, setData] = useState<DashboardData | null>(() => lastData ?? peekPreloaded<DashboardData>('/api/dashboard'))
  const [activity, setActivity] = useState<ActivityEntry[]>(lastActivity)
  const [connected, setConnected] = useState(false)

  useEffect(() => {
    consumer ??= createConsumer('/cable')
    const subscription = consumer.subscriptions.create(
      { channel: 'DashboardChannel' },
      {
        connected: () => setConnected(true),
        disconnected: () => setConnected(false),
        received: (message: DashboardMessage) => {
          if (message.stats) {
            lastData = message.stats
            setData(message.stats)
          }

          if (message.activity) {
            const entry = message.activity
            setActivity((prev) => {
              const next = prev.some((e) => e.id === entry.id) ? prev : [entry, ...prev].slice(0, ACTIVITY_FEED_LIMIT)
              lastActivity = next
              return next
            })
          } else if (message.stats && message.stats.all.recent_activity.length > 0) {
            lastActivity = message.stats.all.recent_activity
            setActivity(message.stats.all.recent_activity)
          }
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  return { data, activity, connected }
}
